import { useEffect, useId, useRef, useState } from 'react';
import { Photo } from '../clients/ClientHybridCard';
import { toneTextStyle } from '../clients/clientsConstants';
import {
    RecordCardActionButton,
    RecordCardProfileTabs,
} from '../shared/RecordCardControls';
import useDisclosureCardClick from '../shared/useDisclosureCardClick';
import { StaffPlacementSummary, SecondaryPlacementEditor } from './StaffPlacements';
import StaffMobileEditor from './StaffMobileEditor';
import ReportsToField, { REPORTS_TO_LABEL, reportsToName, reportsToOptions } from './ReportsToField';
import { formatPhone } from '../../lib/phone';

const inputClass = 'w-full rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-2 py-1.5 text-sm text-[var(--ink)]';

function statusTone(status) {
    if (status === 'active') return 'good';
    if (status === 'on_leave') return 'open';
    if (status === 'terminated') return 'alert';
    return 'muted';
}

function StaffPill({ children, tone = 'muted' }) {
    return (
        <span className="rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-semibold text-[var(--ink)]" style={toneTextStyle(tone)}>
            {children}
        </span>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">{label}</div>
            <div className="text-sm text-[var(--ink)]">{children}</div>
        </div>
    );
}

function ContactActions({ phone, email }) {
    if (!phone && !email) return null;
    return (
        <div className="mt-3 flex gap-2">
            {phone && (
                <a
                    href={`tel:${phone}`}
                    onClick={(event) => event.stopPropagation()}
                    className="ia-hover-pulse rounded-md border border-[var(--surface-border)] bg-transparent px-3 py-1 text-xs font-medium text-[var(--ink)] transition hover:bg-[var(--surface-sunken)]"
                >
                    Call
                </a>
            )}
            {email && (
                <a
                    href={`mailto:${email}`}
                    onClick={(event) => event.stopPropagation()}
                    className="ia-hover-pulse rounded-md border border-[var(--surface-border)] bg-transparent px-3 py-1 text-xs font-medium text-[var(--ink-muted)] transition hover:bg-[var(--surface-sunken)]"
                >
                    Email
                </a>
            )}
        </div>
    );
}

function Person({ member }) {
    return member.person || member.user_profile || {};
}

function PersonName({ member }) {
    const person = Person({ member });
    return person.full_name?.trim() || person.email || 'Person not linked';
}

function PersonPhoto({ member, size }) {
    const person = Person({ member });
    return <Photo contact={{ name: PersonName({ member }), photo_url: person.photo_url || person.avatar_url }} size={size} />;
}

function dispatchStatus(member) {
    if (!member.driver) {
        const person = Person({ member });
        return person.has_driver ? 'Driver linked · status not visible' : 'Not linked to Dispatch';
    }
    return member.driver.status || 'Status unavailable';
}

function appAccessStatus(member) {
    const commandCenterEnabled = member.command_center_enabled ?? Boolean(member.command_center_role);
    const driverAppEnabled = member.driver_app_enabled ?? Boolean(member.driver_id);
    const access = [
        commandCenterEnabled
            ? `Central Command: ${member.command_center_role === 'assignable_admin' ? 'Assignable Admin' : member.command_center_role === 'owner' ? 'Owner' : member.command_center_role === 'user' ? 'User' : member.command_center_role ? 'Admin' : 'Invited'}`
            : null,
        driverAppEnabled
            ? `Driver App: ${!member.user_profile_id ? 'Pending login' : member.driver_status?.replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Invited'}`
            : null,
    ].filter(Boolean);
    return access.length ? access.join(' · ') : 'No app access — Invite';
}

function inviteSentStatus(value) {
    if (!value) return 'No invite sent yet';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Invite time unavailable';
    return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(date);
}

export function StaffTile({
    member, companyLabel, marketLabel, departmentLabel, roleTagLabel, statusLabel, employmentLabel,
    onOpen, entities = [],
}) {
    return (
        <button
            type="button"
            aria-expanded={false}
            onClick={onOpen}
            className="flex min-h-[17rem] w-full flex-col rounded-xl border border-[var(--surface-border)] bg-[var(--surface-raised)] p-5 text-left shadow-sm transition hover:border-brand-500/60"
        >
            <div className="flex items-start gap-3">
                <PersonPhoto member={member} size="sm" />
                <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-[var(--ink)]"><PersonName member={member} /></div>
                    <div className="truncate text-sm text-[var(--ink-muted)]">{member.title || 'Role not set'}</div>
                    <div className="mt-1 truncate text-xs text-[var(--ink-muted)]">
                        {companyLabel(member)}{marketLabel(member) ? ` / ${marketLabel(member)}` : ''}
                    </div>
                </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-1.5">
                <StaffPill tone={statusTone(member.status)}>{statusLabel(member.status)}</StaffPill>
                {member.employment_type && <StaffPill tone="system">{employmentLabel(member.employment_type)}</StaffPill>}
            </div>
            <StaffPlacementSummary member={member} entities={entities} />
            <div className="mt-4 flex-1 space-y-2 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-3 py-2.5">
                <div className="text-[11px] uppercase tracking-wide text-[var(--ink-muted)]">Staff profile</div>
                <div className="truncate text-sm text-[var(--ink)]">{Person({ member }).email || 'No email linked'}</div>
                <div className="truncate text-sm text-[var(--ink-muted)]">{formatPhone(Person({ member }).phone) || 'No mobile linked'}</div>
                <div className="text-xs text-[var(--ink-muted)]">Department · {departmentLabel(member)}</div>
                <div className="text-xs text-[var(--ink-muted)]">Role Tag · {roleTagLabel(member)}</div>
                <div className="text-xs text-[var(--ink-muted)]">App Access · {appAccessStatus(member)}</div>
                <div className="text-xs text-[var(--ink-muted)]">Last invite sent · {inviteSentStatus(member.last_invite_sent_at)}</div>
                <div className="text-xs text-[var(--ink-muted)]">Dispatch · {dispatchStatus(member)}</div>
            </div>
        </button>
    );
}

export function StaffHybridCard({
    member,
    companyLabel,
    marketLabel,
    departmentLabel,
    roleTagLabel,
    statusLabel,
    employmentLabel,
    showIdentity = true,
    onShowMore,
    onCollapseAll,
    entities = [],
    staffDirectory = [],
}) {
    const contentId = useId();
    const { onClick, onDoubleClick, onKeyDown, onPointerDownCapture } = useDisclosureCardClick({
        onOpen: onShowMore,
        onClose: onCollapseAll,
    });
    const person = Person({ member });
    const phone = person.phone;
    const email = person.email;

    return (
        <div
            role="group"
            tabIndex={0}
            className="rounded-xl border border-[var(--surface-border)] bg-[var(--surface-raised)] p-4"
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            onKeyDown={onKeyDown}
            onPointerDownCapture={onPointerDownCapture}
        >
            <button type="button" aria-expanded={false} aria-controls={contentId}
                onClick={onShowMore} className="mb-2 text-left font-semibold text-[var(--ink)]">
                <PersonName member={member} />
            </button>
            <div id={contentId} className="flex gap-4">
                {showIdentity && <PersonPhoto member={member} size="lg" />}
                <div className="min-w-0 flex-1">
                    {showIdentity && (
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="truncate text-sm text-[var(--ink-muted)]">{member.title || 'Role not set'}</div>
                                <div className="truncate text-sm text-[var(--ink-muted)]">
                                    {companyLabel(member)}{marketLabel(member) ? ` / ${marketLabel(member)}` : ''}
                                </div>
                            </div>
                            <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
                                <StaffPill tone={statusTone(member.status)}>{statusLabel(member.status)}</StaffPill>
                                {member.employment_type && <StaffPill tone="system">{employmentLabel(member.employment_type)}</StaffPill>}
                            </div>
                        </div>
                    )}
                    {showIdentity && <div className="flow-rule my-3 h-px w-full opacity-80" />}
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[var(--ink-muted)]">
                        {phone && <span>{formatPhone(phone)}</span>}
                        {email && <span className="truncate">{email}</span>}
                        {!phone && !email && <span className="italic">No mobile or email linked</span>}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[var(--ink-muted)]">
                        <span>Department · {departmentLabel(member)}</span>
                        <span>Reports to · {reportsToName(staffDirectory, member.reports_to_staff_id)}</span>
                        <span>Role Tag · {roleTagLabel(member)}</span>
                        <span>Dispatch · {dispatchStatus(member)}</span>
                        <span>App Access · {appAccessStatus(member)}</span>
                        <span>Last invite sent · {inviteSentStatus(member.last_invite_sent_at)}</span>
                    </div>
                    <ContactActions phone={phone} email={email} />
                    <StaffPlacementSummary member={member} entities={entities} />
                </div>
            </div>
        </div>
    );
}

export function StaffFullCard({
    member,
    entities,
    groups,
    roleTags = [],
    statusOptions,
    employmentOptions,
    canEditTarget = () => true,
    companyLabel,
    marketLabel,
    departmentLabel,
    roleTagLabel,
    statusLabel,
    employmentLabel,
    canEdit,
    editor,
    onCollapse,
    onCollapseAll,
    onOpenEdit,
    onArchive,
    onGiveAppAccess,
    onSendDriverInvite,
    onResendInvite,
    onSendPasswordReset,
    loginActionPending = false,
    canPlaceOrganization = false,
    onChangeEmployeeId,
    staffDirectory = [],
}) {
    const [profileTab, setProfileTab] = useState('overview');
    const [showDeleteChoices, setShowDeleteChoices] = useState(false);
    const [actionError, setActionError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [pendingFocus, setPendingFocus] = useState('');
    const [archiving, setArchiving] = useState(false);
    const [idEditor, setIdEditor] = useState(false);
    const [newId, setNewId] = useState('');
    const [idReason, setIdReason] = useState('');
    const [changingId, setChangingId] = useState(false);
    const placementRefs = useRef({});
    const fullName = PersonName({ member });
    const person = Person({ member });
    const draft = editor.draft;
    const organizationHomeLocked = entities.find((entity) => entity.id === member.entity_id)?.level_type === 'organization'
        && !canPlaceOrganization;
    const companyOptions = entities.filter((entity) => entity.level_type === 'company'
        || (entity.level_type === 'organization' && canPlaceOrganization)
        || (organizationHomeLocked && entity.id === draft.company_id));
    const marketOptions = organizationHomeLocked ? []
        : entities.filter((entity) => entity.level_type === 'market' && entity.parent_entity_id === draft.company_id);

    const update = (key, value) => editor.updateDraft(key, value);
    useEffect(() => {
        if (!pendingFocus) return;
        placementRefs.current[pendingFocus]?.focus();
        setPendingFocus('');
    }, [pendingFocus]);
    const placementFieldProps = (key) => ({
        ref: (node) => { placementRefs.current[key] = node; },
        'aria-invalid': Boolean(fieldErrors[key]),
        'aria-describedby': fieldErrors[key] ? `staff-card-${key}-error` : undefined,
        className: `${inputClass}${fieldErrors[key] ? ' border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`,
        style: fieldErrors[key] ? { borderColor: '#ef4444', outline: '2px solid #ef4444', outlineOffset: '1px' } : undefined,
    });
    const clearPlacementError = (key) => {
        setFieldErrors((current) => {
            if (!current[key]) return current;
            const nextErrors = { ...current };
            delete nextErrors[key];
            return nextErrors;
        });
        if (pendingFocus === key) setPendingFocus('');
    };
    const savePlacement = async () => {
        const errors = {};
        if (!draft.company_id) errors.company_id = 'Choose a Company.';
        if (!draft.title?.trim()) errors.title = 'Title is required.';
        const targetEntityId = organizationHomeLocked
            ? member.entity_id : draft.market_id || draft.company_id || draft.entity_id;
        if (targetEntityId && !canEditTarget(targetEntityId, draft.group_id || null)) {
            const targetField = draft.group_id ? 'group_id' : draft.market_id ? 'market_id' : 'company_id';
            errors[targetField] = 'Choose a placement within your Personnel edit scope.';
        }
        setFieldErrors(errors);
        const firstField = Object.keys(errors)[0];
        if (firstField) {
            setPendingFocus(firstField);
            return;
        }
        await editor.save();
    };
    const startEditing = () => {
        setFieldErrors({});
        setPendingFocus('');
        editor.startEditing();
    };
    const cancelEditing = () => {
        setFieldErrors({});
        setPendingFocus('');
        editor.cancelEditing();
    };
    const runArchive = async () => {
        if (archiving) return;
        setArchiving(true);
        setActionError('');
        try {
            await onArchive();
            setShowDeleteChoices(false);
            onCollapse?.();
        } catch (error) {
            setActionError(error.message || String(error));
        } finally {
            setArchiving(false);
        }
    };
    const contentId = useId();
    const { onClick, onDoubleClick, onKeyDown, onPointerDownCapture, headerRef } = useDisclosureCardClick({
        isOpen: true,
        onClose: onCollapseAll,
        openedAt: editor.openedAt,
    });

    return (
        <div
            role="group"
            tabIndex={0}
            className="peacock-card col-span-full overflow-hidden rounded-lg border border-[var(--surface-border)] shadow-lg"
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            onKeyDown={onKeyDown}
            onPointerDownCapture={onPointerDownCapture}
        >
            <div className="peacock-band h-1.5" />
            <div className="sticky top-0 z-10 border-b border-[var(--surface-border)] bg-[var(--surface-canvas)]/95 px-5 pb-3 pt-4 backdrop-blur">
                <button ref={headerRef} type="button" aria-expanded={true} aria-controls={contentId}
                    className="mb-2 text-left font-semibold text-[var(--ink)]">
                    {fullName}
                </button>
                <div className="flex items-start gap-4">
                    <PersonPhoto member={member} size="lg" />
                    <div className="min-w-0 flex-1">
                        {editor.editing ? (
                            <div className="grid max-w-xl gap-2 sm:grid-cols-2">
                                <label className="space-y-1 text-xs text-[var(--ink-muted)]">
                                    <span>Title</span>
                                    <input {...placementFieldProps('title')} value={draft.title || ''} onChange={(event) => {
                                        clearPlacementError('title');
                                        update('title', event.target.value);
                                    }} />
                                    {fieldErrors.title && <span id="staff-card-title-error" className="block text-xs font-medium text-red-600" role="alert">{fieldErrors.title}</span>}
                                </label>
                            </div>
                        ) : (
                            <>
                                <div className="truncate text-sm text-[var(--ink-muted)]">{member.title || 'Role not set'}</div>
                            </>
                        )}
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--ink-muted)]">
                            <span>{person.email || 'No email linked'}</span>
                            <span>{formatPhone(person.phone) || 'No mobile linked'}</span>
                            {member.employee_id && <span className="font-mono">Employee ID · {member.employee_id}</span>}
                        </div>
                    </div>
                    <div className="flex max-w-[45%] flex-wrap justify-end gap-1.5">
                        <StaffPill tone={statusTone(member.status)}>{statusLabel(editor.editing ? draft.status : member.status)}</StaffPill>
                        {member.employment_type && <StaffPill tone="system">{employmentLabel(editor.editing ? draft.employment_type : member.employment_type)}</StaffPill>}
                    </div>
                </div>
                <RecordCardProfileTabs activeTab={profileTab} onChange={setProfileTab} />
            </div>

            <div id={contentId} className="px-5 py-4">
                {profileTab === 'overview' && (
                    <div className="grid gap-5 md:grid-cols-3">
                        <div className="space-y-3">
                            <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">Person</div>
                            <Field label="Name">{fullName}</Field>
                            <Field label="Email">{person.email || '—'}</Field>
                            <StaffMobileEditor member={member} currentPhone={person.phone} canEdit={Boolean(canEdit)} />
                            <ContactActions phone={person.phone} email={person.email} />
                            {editor.editing && (
                                <RecordCardActionButton onClick={onOpenEdit}>Link or change person</RecordCardActionButton>
                            )}
                        </div>
                        <div className="space-y-3">
                            <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">Role &amp; placement</div>
                            {editor.editing ? (
                                <>
                                    <label className="block space-y-1 text-xs text-[var(--ink-muted)]">
                                        <span>Company</span>
                                        <select {...placementFieldProps('company_id')} value={draft.company_id || ''} disabled={organizationHomeLocked} onChange={(event) => {
                                            const company = entities.find((entity) => entity.id === event.target.value);
                                            clearPlacementError('company_id');
                                            clearPlacementError('market_id');
                                            clearPlacementError('group_id');
                                            update('company_id', company?.id || '');
                                            update('market_id', '');
                                            update('entity_id', event.target.value);
                                            update('group_id', '');
                                        }}>
                                            <option value="">Choose a Company</option>
                                            {companyOptions.map((entity) => <option key={entity.id} value={entity.id}>{entity.name}{entity.level_type === 'organization' ? ' (Organization)' : ''}</option>)}
                                        </select>
                                        {organizationHomeLocked && <span className="block text-xs">Organization placement — Owner/Super Admin can change</span>}
                                        {fieldErrors.company_id && <span id="staff-card-company_id-error" className="block text-xs font-medium text-red-600" role="alert">{fieldErrors.company_id}</span>}
                                    </label>
                                    <SecondaryPlacementEditor entities={entities} homeId={draft.entity_id}
                                        value={draft.secondary_entity_ids || []} canEditTarget={canEditTarget}
                                        onChange={(value) => update('secondary_entity_ids', value)} />
                                    <label className="block space-y-1 text-xs text-[var(--ink-muted)]">
                                        <span>Market</span>
                                        <select {...placementFieldProps('market_id')} value={draft.market_id || ''} disabled={organizationHomeLocked} onChange={(event) => {
                                            clearPlacementError('market_id');
                                            clearPlacementError('group_id');
                                            update('market_id', event.target.value);
                                            update('entity_id', event.target.value || draft.company_id || '');
                                            update('group_id', '');
                                        }}>
                                            <option value="">No Market</option>
                                            {marketOptions.map((entity) => <option key={entity.id} value={entity.id}>{entity.name}</option>)}
                                        </select>
                                        {fieldErrors.market_id && <span id="staff-card-market_id-error" className="block text-xs font-medium text-red-600" role="alert">{fieldErrors.market_id}</span>}
                                    </label>
                                    <label className="block space-y-1 text-xs text-[var(--ink-muted)]">
                                        <span>Department</span>
                                        <select {...placementFieldProps('group_id')} value={draft.group_id || ''} onChange={(event) => {
                                            clearPlacementError('group_id');
                                            update('group_id', event.target.value);
                                        }}>
                                            <option value="">No Department</option>
                                            {groups.filter((group) => group.entity_id === draft.entity_id).map((group) => (
                                                <option key={group.id} value={group.id}>{group.name}</option>
                                            ))}
                                        </select>
                                        {fieldErrors.group_id && <span id="staff-card-group_id-error" className="block text-xs font-medium text-red-600" role="alert">{fieldErrors.group_id}</span>}
                                    </label>
                                    <ReportsToField value={draft.reports_to_staff_id || ''}
                                        onChange={(value) => update('reports_to_staff_id', value)}
                                        canChange={member.reports_to_can_change === true}
                                        options={reportsToOptions(staffDirectory, member.id)}
                                        managerName={reportsToName(staffDirectory, draft.reports_to_staff_id)} />
                                    <label className="block space-y-1 text-xs text-[var(--ink-muted)]">
                                        <span>Role Tag</span>
                                        <select className={inputClass} value={draft.role_tag_id || ''} onChange={(event) => update('role_tag_id', event.target.value)}>
                                            <option value="">No Role Tag</option>
                                            {roleTags.filter((tag) => tag.active || tag.id === draft.role_tag_id).map((tag) => (
                                                <option key={tag.id} value={tag.id}>{tag.name}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="block space-y-1 text-xs text-[var(--ink-muted)]">
                                        <span>Employment type</span>
                                        <select className={inputClass} value={draft.employment_type || ''} onChange={(event) => update('employment_type', event.target.value)}>
                                            {employmentOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                        </select>
                                    </label>
                                    <label className="block space-y-1 text-xs text-[var(--ink-muted)]">
                                        <span>Status</span>
                                        <select className={inputClass} value={draft.status || ''} onChange={(event) => update('status', event.target.value)}>
                                            {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                        </select>
                                    </label>
                                </>
                            ) : (
                                <>
                                    <Field label="Company">{companyLabel(member)}</Field>
                                    <Field label="Market">{marketLabel(member) || '—'}</Field>
                                    <Field label="Department">{departmentLabel(member)}</Field>
                                    <Field label={REPORTS_TO_LABEL}>{reportsToName(staffDirectory, member.reports_to_staff_id)}</Field>
                                    <Field label="Title">{member.title || '—'}</Field>
                                    <Field label="Role Tag">{roleTagLabel(member)}</Field>
                                    <Field label="Employment type">{employmentLabel(member.employment_type)}</Field>
                                    <Field label="Status">{statusLabel(member.status)}</Field>
                                </>
                            )}
                            <StaffPlacementSummary member={member} entities={entities} />
                            {canPlaceOrganization && onChangeEmployeeId && <RecordCardActionButton onClick={() => {
                                setIdEditor(true); setNewId(member.employee_id || ''); setIdReason(''); setActionError('');
                            }}>Change employee ID</RecordCardActionButton>}
                            {idEditor && <form className="space-y-2" onSubmit={async (event) => {
                                event.preventDefault(); setChangingId(true); setActionError('');
                                try { await onChangeEmployeeId(newId.trim(), idReason.trim()); setIdEditor(false); }
                                catch (error) { setActionError(error.message || 'Employee ID could not be changed.'); }
                                finally { setChangingId(false); }
                            }}>
                                <label className="block">New employee ID<input className={inputClass} required pattern="[A-Z0-9]{2,5}-[0-9]{4,}" value={newId} onChange={(event) => setNewId(event.target.value)} /></label>
                                <label className="block">Reason<input className={inputClass} required value={idReason} onChange={(event) => setIdReason(event.target.value)} /></label>
                                <button type="submit" disabled={changingId} className={inputClass}>Save employee ID</button>
                                <button type="button" disabled={changingId} onClick={() => setIdEditor(false)}>Cancel ID change</button>
                            </form>}
                        </div>
                        <div className="space-y-3">
                            <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">Dispatch &amp; app access</div>
                            <Field label="Dispatch status">{dispatchStatus(member)}</Field>
                            <Field label="App Access summary">{appAccessStatus(member)}</Field>
                            <Field label="Last invite sent">{inviteSentStatus(member.last_invite_sent_at)}</Field>
                            {canEdit && (member.command_center_enabled || member.driver_app_enabled) && <>
                                {onResendInvite && <RecordCardActionButton onClick={onResendInvite} disabled={loginActionPending}>Resend invite</RecordCardActionButton>}
                                {member.user_profile_id && onSendPasswordReset &&
                                    <RecordCardActionButton onClick={onSendPasswordReset} disabled={loginActionPending}>Send password reset</RecordCardActionButton>}
                            </>}
                            {canEdit && (member.driver || person.has_driver) && (
                                <RecordCardActionButton onClick={onSendDriverInvite}>Send driver app invite</RecordCardActionButton>
                            )}
                            {canEdit && !member.driver && !person.has_driver && !/\bdriver\b/i.test(member.title || '') && (
                                <RecordCardActionButton onClick={onGiveAppAccess}>Give app access → Permissions &amp; access</RecordCardActionButton>
                            )}
                        </div>
                    </div>
                )}
                {profileTab === 'activity' && (
                    <StaffActivityTimeline member={member} />
                )}
                {profileTab === 'settings' && (
                    <div className="grid gap-5 md:grid-cols-2">
                        <Field label="Person record">{member.person_id ? 'Personnel-only person' : 'Organization member'}</Field>
                        <Field label="Sign-in">{appAccessStatus(member)}</Field>
                        {canEdit && !member.user_profile_id && !member.driver && !person.has_driver
                            && !/\bdriver\b/i.test(member.title || '') && (
                            <RecordCardActionButton onClick={onGiveAppAccess}>Give app access → Permissions &amp; access</RecordCardActionButton>
                        )}
                    </div>
                )}

                {(editor.error || actionError) && (
                    <div className="mt-4 rounded border border-[var(--ui-alert)]/50 bg-[var(--ui-alert)]/10 p-2 text-sm text-[var(--ui-alert)]" role="alert">
                        {editor.error || actionError}
                    </div>
                )}

                {editor.editing && showDeleteChoices && (
                    <div className="mt-4 rounded-lg border border-[var(--ui-alert)]/50 bg-[var(--ui-alert)]/10 p-3">
                        <div className="text-sm font-medium text-[var(--ui-alert)]">Archive this staff record?</div>
                        <div className="mt-1 text-xs text-[var(--ui-alert)]">The record stays in the directory with Inactive status. You can change its status later.</div>
                        <div className="mt-3 flex flex-wrap justify-center gap-2">
                            <RecordCardActionButton onClick={runArchive} disabled={editor.saving || archiving} className="text-[var(--ui-alert)]">
                                {archiving ? 'Archiving…' : 'Archive'}
                            </RecordCardActionButton>
                            <RecordCardActionButton onClick={() => setShowDeleteChoices(false)} disabled={archiving}>
                                Cancel
                            </RecordCardActionButton>
                        </div>
                    </div>
                )}

                <div className="mt-6 flex justify-center gap-3">
                    {editor.editing ? (
                        <>
                            <RecordCardActionButton onClick={savePlacement} disabled={editor.saving || archiving || showDeleteChoices}>
                                {editor.saving ? 'Saving…' : 'Save'}
                            </RecordCardActionButton>
                            <RecordCardActionButton onClick={() => setShowDeleteChoices(true)} disabled={editor.saving || archiving || showDeleteChoices || !canEdit}>
                                Archive
                            </RecordCardActionButton>
                            <RecordCardActionButton onClick={cancelEditing} disabled={editor.saving || archiving}>
                                Cancel
                            </RecordCardActionButton>
                        </>
                    ) : (
                        canEdit && <RecordCardActionButton onClick={startEditing}>Edit</RecordCardActionButton>
                    )}
                </div>
            </div>
        </div>
    );
}

function StaffActivityTimeline({ member }) {
    const updatedAfterCreation = member.updated_at && (
        !member.created_at
        || Math.abs(new Date(member.updated_at).getTime() - new Date(member.created_at).getTime()) > 1000
    );
    const events = [
        { label: 'Staff record created', at: member.created_at },
        ...(updatedAfterCreation
            ? [{ label: 'Staff profile last updated', at: member.updated_at }]
            : []),
    ].filter((event) => event.at);

    return (
        <section className="space-y-3" aria-label="Staff activity timeline">
            <div>
                <h3 className="text-sm font-semibold text-[var(--ink)]">Activity timeline</h3>
                <p className="text-xs text-[var(--ink-muted)]">Staff profile dates recorded by the system.</p>
            </div>
            {events.length ? (
                <ol className="divide-y divide-[var(--surface-border)] rounded-lg border border-[var(--surface-border)]">
                    {events.map((event) => (
                        <li key={event.label} className="flex flex-wrap items-baseline justify-between gap-3 p-3">
                            <span className="text-sm text-[var(--ink)]">{event.label}</span>
                            <time className="text-xs text-[var(--ink-muted)]" dateTime={event.at}>
                                {new Date(event.at).toLocaleString()}
                            </time>
                        </li>
                    ))}
                </ol>
            ) : (
                <p className="rounded-lg border border-[var(--surface-border)] p-4 text-sm text-[var(--ink-muted)]">
                    No staff profile dates are available.
                </p>
            )}
        </section>
    );
}
